'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { getAnalyticsContext, trackPageView } from '@/lib/utils/analytics';
import { AnalyticsContext } from '@/lib/data/types/analytics';

/**
 * Custom hook for analytics functionality
 * Provides access to tracking functions and analytics context
 */
export function useAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [analyticsContext, setAnalyticsContext] = useState<AnalyticsContext | null>(null);

  // Initialize analytics context
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const context = getAnalyticsContext();
      setAnalyticsContext(context);
    }
  }, []);

  // Track custom page view events with structured data for funnel analysis
  // This complements PostHog's automatic $pageview events with additional context
  useEffect(() => {
    if (typeof window !== 'undefined' && pathname) {
      const pageName = getPageNameFromPath(pathname);
      const searchParamsObj = Object.fromEntries(searchParams.entries());
      
      // Track structured page view data for detailed analytics
      // PostHog also automatically captures $pageview events for basic metrics
      trackPageView(pageName, {
        path: pathname,
        search_params: searchParamsObj,
        page_category: getPageCategory(pageName),
        has_filters: Object.keys(searchParamsObj).length > 0,
      });
    }
  }, [pathname, searchParams]);

  // Get current page name from pathname
  const getPageNameFromPath = useCallback((path: string): string => {
    const segments = path.split('/').filter(Boolean);
    
    // Remove locale from segments
    const locales = ['en', 'es', 'de', 'fr', 'it', 'pt', 'nl'];
    if (segments.length > 0 && locales.includes(segments[0])) {
      segments.shift();
    }

    if (segments.length === 0) return 'homepage';
    
    // Common page patterns
    const pagePatterns: Record<string, string> = {
      'playlist-generator': 'playlist_generator',
      'playlists': 'playlists_listing',
      'about': 'about',
      'contact': 'contact',
      'blog': 'blog',
      'my': 'profile',
    };

    const firstSegment = segments[0];
    if (pagePatterns[firstSegment]) {
      // Add specific page context
      if (segments.length > 1) {
        return `${pagePatterns[firstSegment]}_${segments[1]}`;
      }
      return pagePatterns[firstSegment];
    }

    return segments.join('_') || 'unknown';
  }, []);

  // Get page category for analytics grouping
  const getPageCategory = useCallback((pageName: string): string => {
    if (pageName === 'homepage') return 'marketing';
    if (pageName.startsWith('playlist_generator')) return 'generator';
    if (pageName.startsWith('playlists_listing')) return 'discovery';
    if (pageName.startsWith('profile')) return 'user_account';
    if (pageName.startsWith('blog')) return 'content';
    if (pageName.includes('about') || pageName.includes('contact')) return 'information';
    return 'other';
  }, []);

  // Analytics tracking functions
  const trackEvent = useCallback((eventName: string, properties?: Record<string, any>) => {
    if (analyticsContext?.isEnabled) {
      // This is a generic tracking function - specific events should use their dedicated functions
      console.log('Analytics Event:', eventName, properties);
    }
  }, [analyticsContext]);

  return {
    // Context
    isEnabled: analyticsContext?.isEnabled ?? false,
    userId: analyticsContext?.userId,
    sessionId: analyticsContext?.sessionId,
    locale: analyticsContext?.locale ?? 'en',
    
    // Tracking functions
    trackEvent,
    trackPageView: useCallback((pageName: string, properties?: Record<string, any>) => {
      if (analyticsContext?.isEnabled) {
        trackPageView(pageName, properties);
      }
    }, [analyticsContext]),
    
    // Utility functions
    getPageName: useCallback(() => getPageNameFromPath(pathname), [pathname, getPageNameFromPath]),
  };
}

/**
 * Hook for tracking user interactions on current page
 * Provides simplified event tracking with automatic context
 */
export function usePageAnalytics(pageName?: string) {
  const { isEnabled, trackEvent, getPageName } = useAnalytics();
  const currentPageName = pageName || getPageName();

  const trackInteraction = useCallback((
    interactionType: string,
    elementName: string,
    additionalProperties?: Record<string, any>
  ) => {
    if (!isEnabled) return;

    trackEvent(`${currentPageName}_${interactionType}`, {
      element_name: elementName,
      page_name: currentPageName,
      ...additionalProperties,
    });
  }, [isEnabled, trackEvent, currentPageName]);

  const trackClick = useCallback((
    elementName: string,
    additionalProperties?: Record<string, any>
  ) => {
    trackInteraction('click', elementName, additionalProperties);
  }, [trackInteraction]);

  const trackView = useCallback((
    elementName: string,
    additionalProperties?: Record<string, any>
  ) => {
    trackInteraction('view', elementName, additionalProperties);
  }, [trackInteraction]);

  const trackEngagement = useCallback((
    actionType: string,
    elementName: string,
    additionalProperties?: Record<string, any>
  ) => {
    trackInteraction(actionType, elementName, additionalProperties);
  }, [trackInteraction]);

  return {
    isEnabled,
    pageName: currentPageName,
    trackClick,
    trackView,
    trackEngagement,
    trackInteraction,
  };
} 